/*
 * Pocket Popup - audience + engagement gate
 * -----------------------------------------
 * Decides WHO sees the pocket pull popup and WHEN. Loaded immediately BEFORE
 * assets/pocket-pull-engine.js, which is deliberately left untouched: the engine
 * boots by querying the document for [data-pocket-popup], so detaching that element
 * before it runs stops it, and re-attaching it (then calling init) hands the popup
 * back. The section runs the engine with a 0s delay, so release == open.
 *
 * WHO: new customers only. Held back from
 *   - anyone Klaviyo already knows (identified profile, __kla_id, Klaviyo link)
 *   - previous buyers (rnd_buyer flag, written by the checkout_completed pixel)
 *   - anyone who has ever arrived from an email or SMS link (remembered on the browser)
 *   - logged-in customers (the engine's own account-holder check)
 *
 * WHEN: v170 opened on page load, which on mobile was a full-screen takeover sitting
 * on top of Add to Cart for every first visit. Now it waits for intent:
 *   - ENGAGED_SECONDS of visible time, added up across the session, AND
 *   - a SCROLL_DEPTH scroll on this page OR a second pageview, THEN
 *   - at least 3s on the current page, so it never slaps a page the moment it loads,
 *   - a pause (no scroll/tap for QUIET_MS) so it never lands mid-gesture.
 *   - Desktop exit intent (pointer leaving toward the tab bar) after 5s also opens it.
 * Never while a drawer/menu/form field is in use, never on cart/account pages, never
 * for a shopper with items in the cart, and never on product pages (exit intent
 * included). Time on product pages still counts toward engaged time.
 *
 * Product-page entrances (paid social, shopping ads) came for that product, so they
 * get room to shop first: a session that LANDED on a product page does not qualify
 * until its PRODUCT_ENTRY_MIN_PAGEVIEWS-th pageview (3). With the dwell and pause
 * rules on top, that lands in practice on page 3 or 4.
 *
 * QA: ?popup=reset clears suppression and skips the audience checks (trigger still
 * applies). ?popup=now opens immediately. ?popup=debug logs every decision.
 *
 * Every Klaviyo path still fails OPEN: an unanswerable "is this a subscriber" shows
 * the popup rather than silently disabling the channel.
 *
 * Everything above the controller is pure and runs under `node --test`.
 *
 * Analytics: every decision and step is reported through PocketPopupAnalytics
 * (defined above the gate in this same file, so both ship as one asset).
 */
/*
 * Pocket Popup - analytics + attribution
 * --------------------------------------
 * Observes the popup from the outside (the engine stays untouched) and turns every
 * step into a structured event with full session context, then fans it out to:
 *
 *   1. HQ collector      sendBeacon batches -> ripndip-popup-events Worker -> D1
 *   2. PostHog           posthog.capture('pp_<event>', props)
 *   3. Shopify events    Shopify.analytics.publish('pocket_popup', props), so a
 *                        customer-events pixel can forward to GA4/Meta/Polar
 *
 * Attribution written on the way:
 *   - cart attributes    _pp_exposed on first open, _pp_signup on email success.
 *                        Underscore keys are hidden at checkout but land on the order
 *                        as note_attributes, which Polar and Shopify can segment on.
 *   - Klaviyo profile    identify() with pp_* properties + a "Pocket Popup Signup"
 *                        event, so flows/segments can split by trigger, page, UTM.
 *
 * Event names (all carry the common context from context()):
 *   pp_session_decision  once per session: audience outcome (ok / skipped + reason)
 *   pp_triggered         the gate released it (via engaged | exit-intent | now)
 *   pp_held              wanted to open but a cart had items
 *   pp_shown             overlay actually opened (ms_to_open)
 *   pp_pull_started      first touch on the pocket
 *   pp_pull_completed    pocket fully pulled (pull_ms)
 *   pp_skip_clicked      "skip and just get the discount"
 *   pp_capture_viewed    email step on screen (path: pulled | skipped)
 *   pp_email_focus       first focus in the email field
 *   pp_email_submit      submit pressed (valid)
 *   pp_email_error       error shown (message)
 *   pp_email_success     Klaviyo accepted the email
 *   pp_sms_viewed / pp_sms_submit / pp_sms_error / pp_sms_success / pp_sms_skipped
 *   pp_done              success screen
 *   pp_dismissed         closed without finishing (from_state, method, open_ms)
 *   pp_closed            closed after finishing (open_ms)
 *
 * Privacy: nothing is sent when Shopify's customer privacy API reports analytics
 * processing as explicitly denied. The email address is never sent to the collector,
 * PostHog or Shopify events, only to Klaviyo (which already receives it).
 */
var PocketPopupAnalytics = (function () {
  'use strict';

  var VERSION = 'v171';
  var DEFAULT_COLLECTOR = 'https://events.ripndip.com/pp';
  var SS_SID = 'rnd_pp_sid';
  var SS_LANDING = 'rnd_pp_landing';
  var SS_DECIDED = 'rnd_pp_decided';
  var LS_VID = 'rnd_pp_vid';

  var win = typeof window !== 'undefined' ? window : null;
  var doc = typeof document !== 'undefined' ? document : null;
  var queue = [];
  var flushTimer = null;
  var cfg = { collector: DEFAULT_COLLECTOR, variant: 'pull10-headline' };
  var ctxExtra = {};

  function uuid() {
    try { if (win.crypto && win.crypto.randomUUID) { return win.crypto.randomUUID(); } } catch (e) {}
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }
  function ssGet(k) { try { return win.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { win.sessionStorage.setItem(k, v); } catch (e) {} }
  function lsGet(k) { try { return win.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { win.localStorage.setItem(k, v); } catch (e) {} }
  function cookie(name) {
    var m = String(doc.cookie || '').match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  /* Pure: pull the attribution fields out of a landing URL query string. */
  function parseLanding(search, referrer) {
    var out = { utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '', utm_term: '',
      click_id: '', referrer_host: '' };
    var s = String(search || '').replace(/^\?/, '');
    s.split('&').forEach(function (pair) {
      if (!pair) { return; }
      var i = pair.indexOf('=');
      var k = decodeURIComponent((i < 0 ? pair : pair.slice(0, i)).replace(/\+/g, ' ')).toLowerCase();
      var v = i < 0 ? '' : decodeURIComponent(pair.slice(i + 1).replace(/\+/g, ' '));
      if (Object.prototype.hasOwnProperty.call(out, k) && k.indexOf('utm_') === 0) { out[k] = v.slice(0, 120); }
      if (!out.click_id && /^(fbclid|gclid|ttclid|msclkid|sccid|epik)$/.test(k)) { out.click_id = k; }
    });
    try { if (referrer) { out.referrer_host = new URL(referrer).hostname.replace(/^www\./, ''); } } catch (e) {}
    return out;
  }

  function landing() {
    var raw = ssGet(SS_LANDING);
    if (raw) { try { return JSON.parse(raw); } catch (e) {} }
    var l = parseLanding(win.location.search, doc.referrer);
    l.landing_path = win.location.pathname;
    ssSet(SS_LANDING, JSON.stringify(l));
    return l;
  }

  function analyticsAllowed() {
    try {
      var cp = win.Shopify && win.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed() !== false;
      }
    } catch (e) {}
    return true;
  }

  function context() {
    var sid = ssGet(SS_SID);
    if (!sid) { sid = uuid(); ssSet(SS_SID, sid); }
    var vid = cookie('_shopify_y') || lsGet(LS_VID);
    if (!vid) { vid = uuid(); lsSet(LS_VID, vid); }
    var st = win.PocketPopupGateState || {};
    var l = landing();
    var fine = false;
    try { fine = win.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch (e) {}
    var pageType = '';
    try { pageType = win.ShopifyAnalytics.meta.page.pageType; } catch (e) {}
    var c = {
      session_id: sid,
      visitor_id: vid,
      popup_version: VERSION,
      variant: cfg.variant,
      theme_id: (win.Shopify && win.Shopify.theme && win.Shopify.theme.id) || null,
      theme_role: (win.Shopify && win.Shopify.theme && win.Shopify.theme.role) || '',
      page_type: pageType || '',
      path: win.location.pathname,
      entry_type: st.entry || '',
      pageviews: st.pageviews || null,
      active_s: st.activeMs ? Math.round(st.activeMs / 1000) : null,
      depth_pct: st.depth ? Math.round(st.depth * 100) : null,
      device: fine ? 'desktop' : 'mobile',
      viewport_w: win.innerWidth,
      country: (win.Shopify && win.Shopify.country) || '',
      locale: (win.Shopify && win.Shopify.locale) || '',
      currency: (win.Shopify && win.Shopify.currency && win.Shopify.currency.active) || ''
    };
    Object.keys(l).forEach(function (k) { c[k] = l[k]; });
    Object.keys(ctxExtra).forEach(function (k) { c[k] = ctxExtra[k]; });
    return c;
  }

  function flush(useBeacon) {
    if (flushTimer) { win.clearTimeout(flushTimer); flushTimer = null; }
    if (!queue.length || !cfg.collector) { return; }
    var batch = queue.splice(0, 50);
    var body = JSON.stringify({ v: 1, events: batch });
    var sent = false;
    try {
      if (useBeacon !== false && win.navigator.sendBeacon) {
        /* text/plain keeps it a simple request: no CORS preflight. */
        sent = win.navigator.sendBeacon(cfg.collector, new Blob([body], { type: 'text/plain' }));
      }
    } catch (e) {}
    if (!sent) {
      try { win.fetch(cfg.collector, { method: 'POST', body: body, keepalive: true, mode: 'no-cors',
        headers: { 'content-type': 'text/plain' } }); } catch (e) {}
    }
    if (queue.length) { flush(useBeacon); }
  }

  function track(name, props) {
    if (!win || !analyticsAllowed()) { return; }
    var ev = context();
    ev.event = name;
    ev.event_id = uuid();
    ev.ts = new Date().toISOString();
    if (props) { Object.keys(props).forEach(function (k) { ev[k] = props[k]; }); }

    queue.push(ev);
    if (!flushTimer) { flushTimer = win.setTimeout(function () { flush(true); }, 2000); }

    try { if (win.posthog && win.posthog.capture) { win.posthog.capture(name, ev); } } catch (e) {}
    try {
      if (win.Shopify && win.Shopify.analytics && win.Shopify.analytics.publish) {
        win.Shopify.analytics.publish('pocket_popup', ev);
      }
    } catch (e) {}
    if (/[?&]popup=debug\b/.test(win.location.search)) {
      try { win.console.info('[pocket-popup] ' + name, ev); } catch (e) {}
    }
  }

  /* Once per session: who was this visitor, as far as the popup is concerned. */
  function decision(audience, reason) {
    if (ssGet(SS_DECIDED)) { return; }
    ssSet(SS_DECIDED, '1');
    track('pp_session_decision', { audience: audience, reason: reason || '' });
  }

  function setCartAttributes(attrs) {
    try {
      win.fetch('/cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function () {});
    } catch (e) {}
  }

  function today() { return new Date().toISOString().slice(0, 10); }

  function stampKlaviyo(email, via) {
    var c = context();
    var props = {
      pp_variant: c.variant,
      pp_version: c.popup_version,
      pp_trigger: via || '',
      pp_signup_path: c.path,
      pp_signup_page_type: c.page_type,
      pp_entry_type: c.entry_type,
      pp_landing_path: c.landing_path || '',
      pp_utm_source: c.utm_source,
      pp_utm_medium: c.utm_medium,
      pp_utm_campaign: c.utm_campaign,
      pp_referrer_host: c.referrer_host,
      pp_device: c.device,
      pp_session_id: c.session_id,
      pp_signup_date: today()
    };
    try {
      var k = win.klaviyo;
      if (k && typeof k.identify === 'function') {
        var idProps = { email: email };
        Object.keys(props).forEach(function (p) { idProps[p] = props[p]; });
        var r = k.identify(idProps);
        var send = function () { try { k.track('Pocket Popup Signup', props); } catch (e) {} };
        if (r && typeof r.then === 'function') { r.then(send, send); } else { send(); }
      } else if (k && typeof k.push === 'function') {
        var q = { $email: email };
        Object.keys(props).forEach(function (p) { q[p] = props[p]; });
        k.push(['identify', q]);
        k.push(['track', 'Pocket Popup Signup', props]);
      }
    } catch (e) {}
  }

  /* ---- DOM observer: reads engine state without touching the engine ---- */
  function observe(root, via) {
    if (!root || root.dataset.ppObserved) { return; }
    root.dataset.ppObserved = '1';
    ctxExtra.trigger_via = via || '';

    var releasedAt = Date.now();
    var openedAt = 0;
    var pullStartedAt = 0;
    var state = 'idle';
    var reached = {};
    var lastIntent = '';
    var emailFocused = false;
    var capturedEmail = '';
    var finished = false;
    var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    function visiblePanel() {
      var p = [].slice.call(root.querySelectorAll('[data-panel]')).filter(function (n) { return !n.hidden; });
      return p.length ? p[0].getAttribute('data-panel') : '';
    }

    function onPanel(next) {
      if (!next || next === state) { return; }
      var prev = state;
      state = next;
      if (next === 'pulled') {
        track('pp_pull_completed', { pull_ms: pullStartedAt ? Date.now() - pullStartedAt : null });
      } else if (next === 'capture' && !reached.capture) {
        track('pp_capture_viewed', { path: prev === 'pulled' ? 'pulled' : 'skipped' });
      } else if (next === 'sms' && !reached.sms) {
        emailSuccess();
        track('pp_sms_viewed');
      } else if (next === 'done' && !reached.done) {
        if (prev === 'capture') { emailSuccess(); }
        if (prev === 'sms') {
          if (lastIntent === 'sms-skip') { track('pp_sms_skipped'); }
          else { track('pp_sms_success'); }
        }
        finished = true;
        track('pp_done');
      }
      reached[next] = true;
    }

    function emailSuccess() {
      if (reached.emailSuccess) { return; }
      reached.emailSuccess = true;
      finished = true;
      track('pp_email_success', { open_ms: openedAt ? Date.now() - openedAt : null });
      var v = cfg.variant + '|' + (via || '') + '|' + today();
      setCartAttributes({ _pp_signup: v, _pp_sid: context().session_id });
      if (capturedEmail) { stampKlaviyo(capturedEmail, via); }
    }

    var mo = new MutationObserver(function (records) {
      records.forEach(function (r) {
        var t = r.target;
        if (t === root && r.attributeName === 'class') {
          if (root.classList.contains('is-open') && !openedAt) {
            openedAt = Date.now();
            track('pp_shown', { ms_to_open: openedAt - releasedAt });
            setCartAttributes({ _pp_exposed: cfg.variant + '|' + today() });
          }
        }
        if (t === root && r.attributeName === 'hidden' && root.hidden && openedAt) {
          var openMs = Date.now() - openedAt;
          if (finished) { track('pp_closed', { open_ms: openMs, final_state: state }); }
          else { track('pp_dismissed', { from_state: state, method: lastIntent || 'unknown', open_ms: openMs }); }
          mo.disconnect();
          flush(true);
          return;
        }
        if (r.attributeName === 'hidden' && t.hasAttribute && t.hasAttribute('data-panel')) {
          onPanel(visiblePanel());
        }
        if (r.attributeName === 'class' && t.hasAttribute && t.hasAttribute('data-hotspot')) {
          if (t.classList.contains('is-dragging') && !pullStartedAt) {
            pullStartedAt = Date.now();
            track('pp_pull_started');
          }
        }
        if (r.attributeName === 'hidden' && t.hasAttribute && !t.hidden &&
            (t.hasAttribute('data-error') || t.hasAttribute('data-sms-error'))) {
          /* The engine re-hides then re-shows the same node per submit, which arrives as
             a burst of records; one error per visible showing. */
          if (t.dataset.ppErrShown === '1') { return; }
          t.dataset.ppErrShown = '1';
          win.setTimeout(function () { t.dataset.ppErrShown = ''; }, 400);
          track(t.hasAttribute('data-error') ? 'pp_email_error' : 'pp_sms_error', { message: (t.textContent || '').slice(0, 80) });
        }
      });
    });
    mo.observe(root, { attributes: true, subtree: true, attributeFilter: ['class', 'hidden'] });

    root.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-close],[data-overlay],[data-skip],[data-sms-skip]') : null;
      if (!el) { return; }
      if (el.hasAttribute('data-close')) { lastIntent = 'close-button'; }
      else if (el.hasAttribute('data-overlay')) { lastIntent = 'overlay'; }
      else if (el.hasAttribute('data-skip')) { lastIntent = 'skip'; track('pp_skip_clicked'); }
      else if (el.hasAttribute('data-sms-skip')) { lastIntent = 'sms-skip'; }
    }, true);

    doc.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !root.hidden) { lastIntent = 'escape'; }
    }, true);

    root.addEventListener('focusin', function (e) {
      if (!emailFocused && e.target && e.target.hasAttribute && e.target.hasAttribute('data-email')) {
        emailFocused = true;
        track('pp_email_focus');
      }
    });

    root.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.hasAttribute) { return; }
      if (form.hasAttribute('data-form')) {
        var input = root.querySelector('[data-email]');
        var val = input ? String(input.value || '').trim() : '';
        var valid = EMAIL_RE.test(val);
        if (valid) { capturedEmail = val; }
        track('pp_email_submit', { valid: valid });
      } else if (form.hasAttribute('data-sms-form')) {
        lastIntent = 'sms-submit';
        track('pp_sms_submit');
      }
    }, true);
  }

  function configure(el) {
    if (!el) { return; }
    var c = el.getAttribute('data-collector');
    if (c !== null) { cfg.collector = c; }
    var v = el.getAttribute('data-variant');
    if (v) { cfg.variant = v; }
  }

  if (win) {
    win.addEventListener('pagehide', function () { flush(true); });
    doc.addEventListener('visibilitychange', function () { if (doc.visibilityState === 'hidden') { flush(true); } });
  }

  return {
    VERSION: VERSION,
    configure: configure,
    track: track,
    decision: decision,
    observe: observe,
    flush: flush,
    parseLanding: parseLanding
  };
})();

(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
  else { root.PocketPopupKlaviyoGate = api; }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var KNOWN_KEY = 'rnd_pocket_popup_kl_known';
  var PROBE_MS = 1200;

  /* Defaults. Each can be overridden per section with a data attribute of the same
     name (data-engaged-seconds="15") without touching this file. */
  var DEFAULTS = {
    engagedSeconds: 12,
    scrollDepth: 40,
    quietMs: 900,
    minPageSeconds: 3,
    exitIntent: true,
    exitMinSeconds: 5,
    skipProductPages: true,
    skipWithCart: true,
    skipOwnedChannels: true,
    skipPreviousBuyers: true,
    productEntryMinPageviews: 3
  };

  var SUPPRESS_COOKIE = 'rnd_pocket_popup';
  var SS_ACTIVE = 'rnd_pp_active_ms';
  var SS_PV = 'rnd_pp_pv';
  var SS_CARTED = 'rnd_pp_carted';
  var SS_ENTRY = 'rnd_pp_entry';
  var LS_OWNED = 'rnd_pp_owned';
  var LS_BUYER = 'rnd_buyer';

  function A() { return typeof PocketPopupAnalytics !== 'undefined' ? PocketPopupAnalytics : null; }
  function aTrack(name, props) { var a = A(); if (a) { a.track(name, props); } }
  function aDecision(audience, reason) { var a = A(); if (a) { a.decision(audience, reason); } }

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  /* __kla_id is base64 of a small JSON blob. Keyed on the contact details inside
     it and deliberately NOT on $exchange_id: Klaviyo mints an exchange id for
     anonymous browsers too, so treating that as "known" would suppress the popup
     for every visitor on earth and look like the section was broken. */
  function cookieKnown(raw, b64decode) {
    if (!raw) { return false; }
    try {
      var txt = String(raw);
      try { txt = decodeURIComponent(txt); } catch (e) {}
      var obj = JSON.parse(b64decode ? b64decode(txt) : txt);
      if (!obj || typeof obj !== 'object') { return false; }
      return !!(obj.$email || obj.$phone_number);
    } catch (e) { return false; }
  }

  function linkKnown(search) {
    var s = String(search || '').toLowerCase();
    return /[?&]_kx=/.test(s) || /[?&]utm_source=klaviyo(?:[&#]|$)/.test(s);
  }

  function readCookie(cookie, name) {
    var m = String(cookie || '').match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? m[1] : '';
  }

  /* Email and SMS clicks from any provider. Only a subscriber can be sent one.
     Attentive links land with utm_source=attentive / utm_medium=text. */
  var OWNED_RE = /[?&](?:_kx=|utm_medium=(?:email|sms|text|push)(?:[&#]|$)|utm_source=(?:klaviyo|attentive|postscript|sms)(?:[&#]|$))/;
  function ownedChannelLanding(search) {
    return OWNED_RE.test(String(search || '').toLowerCase());
  }

  /* Fraction of the page seen. A page that barely scrolls counts as fully seen,
     otherwise short pages could never qualify on depth. */
  function scrollDepth(scrollY, viewportH, docH) {
    if (!docH || !viewportH || docH <= viewportH * 1.15) { return 1; }
    return clamp((scrollY + viewportH) / docH, 0, 1);
  }

  function engagementMet(o) {
    if (!(o.activeMs >= o.needMs)) { return false; }
    return o.depth >= o.needDepth || o.pageviews >= 2;
  }

  function quietFor(now, lastInteraction, quietMs) {
    return now - lastInteraction >= quietMs;
  }

  /* Pages where a signup interruption costs more than it earns. */
  function blockedPage(path, pageType, skipProduct) {
    var p = String(path || '').toLowerCase();
    var t = String(pageType || '').toLowerCase();
    if (t === 'cart' || /\/(?:cart|account|checkouts?)(?:\/|$)/.test(p)) { return 'cart-or-account'; }
    if (skipProduct && (t === 'product' || /\/products\//.test(p))) { return 'product'; }
    return '';
  }

  /* A session that entered on a product page waits for a later pageview. */
  function entryAllows(entry, pageviews, minPageviews) {
    return entry !== 'product' || pageviews >= minPageviews;
  }

  function drawerOpen(className) {
    return /(?:^|\s)(?:js-drawer-open|modal-open)(?:\s|-|$)/.test(String(className || ''));
  }

  /* ---- Browser controller. Everything above this line is pure. ---- */

  function known(doc, win) {
    try { if (win.localStorage.getItem(KNOWN_KEY)) { return true; } } catch (e) {}
    if (linkKnown(win.location && win.location.search)) { return true; }
    var b64 = null;
    try { if (typeof win.atob === 'function') { b64 = function (t) { return win.atob(t); }; } } catch (e) {}
    return cookieKnown(readCookie(doc.cookie, '__kla_id'), b64);
  }

  function remember(win) { try { win.localStorage.setItem(KNOWN_KEY, '1'); } catch (e) {} }
  function forget(win) { try { win.localStorage.removeItem(KNOWN_KEY); } catch (e) {} }

  /* window.klaviyo is an array-shaped push queue until klaviyo.js swaps in the real
     object, and that script is loaded by the Klaviyo app embed, not by the theme, so
     it may land after this runs. Poll briefly for the real method, then give up and
     report "not known" rather than holding the popup shut. */
  function askIdentified(win, cb) {
    var done = false;
    function finish(v) { if (!done) { done = true; cb(!!v); } }
    function attempt() {
      try {
        var k = win.klaviyo;
        if (!k || typeof k.isIdentified !== 'function') { return false; }
        var r = k.isIdentified();
        if (r && typeof r.then === 'function') { r.then(finish, function () { finish(false); }); }
        else { finish(r); }
        return true;
      } catch (e) { finish(false); return true; }
    }
    if (attempt()) { return; }
    var waited = 0;
    var iv = win.setInterval(function () {
      waited += 100;
      if (attempt() || waited >= PROBE_MS) {
        win.clearInterval(iv);
        if (!done) { finish(false); }
      }
    }, 100);
  }

  function readConfig(el) {
    var cfg = {};
    Object.keys(DEFAULTS).forEach(function (k) {
      var attr = 'data-' + k.replace(/[A-Z]/g, function (c) { return '-' + c.toLowerCase(); });
      var v = el.getAttribute(attr);
      if (v === null || v === '') { cfg[k] = DEFAULTS[k]; }
      else if (typeof DEFAULTS[k] === 'boolean') { cfg[k] = v === 'true'; }
      else { var n = parseFloat(v); cfg[k] = isNaN(n) ? DEFAULTS[k] : n; }
    });
    return cfg;
  }

  function gate(el) {
    if (!el || el.dataset.ppGate) { return; }
    el.dataset.ppGate = '1';

    var doc = el.ownerDocument;
    var win = doc.defaultView;
    var search = '';
    try { search = win.location.search || ''; } catch (e) {}

    /* The merchant is looking at it on purpose. */
    if (win.Shopify && win.Shopify.designMode) { return; }

    var reset = /[?&]popup=reset\b/.test(search);
    var openNow = /[?&]popup=now\b/.test(search);
    var debug = /[?&]popup=debug\b/.test(search) &&
      !(win.Shopify && win.Shopify.theme && win.Shopify.theme.role === 'main');
    if (reset) { forget(win); }

    var cfg = readConfig(el);
    if (A()) { A().configure(el); }

    function ssGet(k) { try { return win.sessionStorage.getItem(k); } catch (e) { return null; } }
    function ssSet(k, v) { try { win.sessionStorage.setItem(k, v); } catch (e) {} }
    function lsGet(k) { try { return win.localStorage.getItem(k); } catch (e) { return null; } }
    function lsSet(k, v) { try { win.localStorage.setItem(k, v); } catch (e) {} }
    function log() {
      if (!debug) { return; }
      try { win.console.info.apply(win.console, ['[pocket-popup]'].concat([].slice.call(arguments))); } catch (e) {}
    }

    /* Bookkeeping that must happen on every pageview, including ones we skip. */
    var pageviews = (parseInt(ssGet(SS_PV), 10) || 0) + 1;
    ssSet(SS_PV, String(pageviews));
    var pageType = '';
    try { pageType = win.ShopifyAnalytics.meta.page.pageType; } catch (e) {}
    var path = '';
    try { path = win.location.pathname; } catch (e) {}
    /* Remember what kind of page the session started on. Recorded before any skip, so
       a suppressed first pageview still sets it. */
    var entry = ssGet(SS_ENTRY);
    if (pageviews === 1 || !entry) {
      entry = blockedPage(path, pageType, true) === 'product' ? 'product' : 'other';
      ssSet(SS_ENTRY, entry);
    }
    if (ownedChannelLanding(search)) { lsSet(LS_OWNED, '1'); }
    function markCarted() { ssSet(SS_CARTED, '1'); }
    doc.addEventListener('ajaxProduct:added', markCarted);
    doc.addEventListener('cart:added', markCarted);

    var parent = el.parentNode;
    if (!parent) { return; }

    /* Already dismissed or subscribed on this browser: the engine would no-op anyway,
       so leave it attached and skip all tracking work. */
    var until = parseInt(readCookie(doc.cookie, SUPPRESS_COOKIE), 10);
    if (!reset && !openNow && until && Date.now() < until) {
      log('skip: suppressed until', new Date(until)); aDecision('skipped', 'suppressed'); return;
    }

    /* Detach rather than hide. The engine boots off a document query, so an element
       that is not in the document is an engine that never starts. */
    var mark = doc.createComment('pocket-popup-gated');
    parent.replaceChild(mark, el);

    var state = {
      activeMs: parseInt(ssGet(SS_ACTIVE), 10) || 0,
      depth: 0,
      pageviews: pageviews,
      audience: 'pending',
      entry: entry,
      fired: false,
      via: '',
      page: ''
    };
    win.PocketPopupGateState = state;

    function release(via) {
      if (!mark.parentNode) { return; }
      state.fired = true;
      state.via = via;
      mark.parentNode.replaceChild(el, mark);
      log('release via', via, JSON.stringify(state));
      /* Observer first, so it sees the engine add is-open. */
      if (A()) { A().observe(el, via); }
      aTrack('pp_triggered', { via: via, page_ms: state.pageMs || null });
      /* Either the engine has not run yet, in which case its own boot finds the
         element back in place, or it has, in which case it found nothing and needs
         telling. init() is idempotent, so doing both is safe. */
      try {
        if (win.PocketPullEngine && win.PocketPullEngine.init) { win.PocketPullEngine.init(el); }
      } catch (e) {}
    }

    if (openNow) { release('now'); return; }

    /* ---- WHO ---- */
    if (!reset) {
      if (el.getAttribute('data-logged-in') === 'true' && el.getAttribute('data-disable-accounts') === 'true') {
        log('skip: logged-in customer'); aDecision('skipped', 'logged-in'); return;
      }
      if (cfg.skipPreviousBuyers && (lsGet(LS_BUYER) || /(?:^|; )rnd_buyer=1/.test(doc.cookie || ''))) {
        log('skip: previous buyer'); aDecision('skipped', 'previous-buyer'); return;
      }
      if (cfg.skipOwnedChannels && lsGet(LS_OWNED)) { log('skip: email/sms traffic'); aDecision('skipped', 'owned-channel'); return; }
    }

    /* ---- WHEN: engagement tracking starts now, firing waits for the audience ---- */
    var lastInteraction = Date.now();
    var lastTick = Date.now();
    var pageActiveMs = 0;
    var cartChecking = false;
    state.page = pageType || '';

    function measureDepth() {
      var d = doc.documentElement;
      var docH = Math.max(d.scrollHeight, doc.body ? doc.body.scrollHeight : 0);
      var depth = scrollDepth(win.pageYOffset || d.scrollTop || 0, win.innerHeight || d.clientHeight, docH);
      if (depth > state.depth) { state.depth = depth; }
    }

    function busy() {
      if (drawerOpen(doc.documentElement.className) || (doc.body && drawerOpen(doc.body.className))) { return true; }
      var a = doc.activeElement;
      return !!(a && a !== doc.body && /^(?:INPUT|TEXTAREA|SELECT)$/.test(a.tagName));
    }

    function cartHasItems() {
      try {
        return win.fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (d) { return !!(d && d.item_count > 0); })
          .catch(function () { return false; });
      } catch (e) { return Promise.resolve(false); }
    }

    /* Shopper with a cart is mid-purchase: never stand between them and checkout. */
    function attempt(via) {
      if (state.fired || state.audience !== 'ok') { return; }
      if (!reset && !entryAllows(entry, state.pageviews, cfg.productEntryMinPageviews)) { return; }
      if (!reset && cfg.skipWithCart) {
        if (ssGet(SS_CARTED)) { return; }
        if (cartChecking) { return; }
        cartChecking = true;
        cartHasItems().then(function (has) {
          cartChecking = false;
          if (has) { markCarted(); log('hold: cart has items'); aTrack('pp_held', { reason: 'cart', via: via }); return; }
          release(via);
        });
        return;
      }
      release(via);
    }

    ['scroll', 'wheel', 'touchstart', 'touchmove', 'pointerdown', 'keydown'].forEach(function (ev) {
      win.addEventListener(ev, function () {
        lastInteraction = Date.now();
        if (ev === 'scroll' || ev === 'wheel' || ev === 'touchmove') { measureDepth(); }
      }, { passive: true, capture: true });
    });
    win.addEventListener('load', measureDepth);
    measureDepth();

    var blocked = reset ? '' : blockedPage(path, pageType, cfg.skipProductPages);
    var iv = win.setInterval(function () {
      var now = Date.now();
      if (doc.visibilityState === 'visible') {
        state.activeMs += Math.min(now - lastTick, 1000);
        pageActiveMs += Math.min(now - lastTick, 1000);
        state.pageMs = pageActiveMs;
        ssSet(SS_ACTIVE, String(state.activeMs));
      }
      lastTick = now;
      if (state.fired) { win.clearInterval(iv); return; }
      if (blocked || state.audience !== 'ok' || pageActiveMs < cfg.minPageSeconds * 1000) { return; }
      if (!engagementMet({
        activeMs: state.activeMs, needMs: cfg.engagedSeconds * 1000,
        depth: state.depth, needDepth: cfg.scrollDepth / 100, pageviews: state.pageviews
      })) { return; }
      if (!quietFor(now, lastInteraction, cfg.quietMs) || busy()) { return; }
      attempt('engaged');
    }, 250);

    /* Desktop only: a pointer heading for the tab bar or address bar. Same page and
       product-entry rules as the engaged trigger. */
    var finePointer = false;
    try { finePointer = !!(win.matchMedia && win.matchMedia('(hover: hover) and (pointer: fine)').matches); } catch (e) {}
    if (cfg.exitIntent && finePointer && !blocked) {
      doc.addEventListener('mouseout', function (e) {
        if (e.relatedTarget || e.clientY > 0) { return; }
        if (state.activeMs < cfg.exitMinSeconds * 1000 || busy()) { return; }
        attempt('exit-intent');
      });
    }

    if (blocked) { log('no popup on this page:', blocked, '(time still counts)'); }
    if (!entryAllows(entry, pageviews, cfg.productEntryMinPageviews)) {
      log('product-page entrance: waiting for pageview', cfg.productEntryMinPageviews, '(now', pageviews + ')');
    }

    /* ---- Klaviyo audience check ---- */
    if (reset || el.getAttribute('data-skip-known') === 'false') { state.audience = 'ok'; aDecision('eligible', reset ? 'reset' : ''); return; }
    if (known(doc, win)) {
      state.audience = 'klaviyo-known'; win.clearInterval(iv); log('skip: klaviyo known'); aDecision('skipped', 'klaviyo-known'); return;
    }

    /* askIdentified caps itself, but a callback that never fires must not leave the
       popup permanently held. Armed first because askIdentified can answer
       synchronously, and cancelled on any answer. v170 let this fire even after
       Klaviyo said "identified", so known subscribers still got the popup. */
    var fallback = win.setTimeout(function () {
      if (state.audience === 'pending') { state.audience = 'ok'; aDecision('eligible', 'klaviyo-timeout'); }
    }, PROBE_MS + 400);
    askIdentified(win, function (identified) {
      win.clearTimeout(fallback);
      if (identified) {
        remember(win);
        state.audience = 'klaviyo-identified';
        win.clearInterval(iv);
        log('skip: klaviyo identified');
        aDecision('skipped', 'klaviyo-identified');
        return;
      }
      state.audience = 'ok';
      aDecision('eligible', '');
    });
  }

  function boot() {
    if (typeof document === 'undefined') { return; }
    [].slice.call(document.querySelectorAll('[data-pocket-popup]')).forEach(gate);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); }
    else { boot(); }
    document.addEventListener('shopify:section:load', boot);
  }

  return {
    KNOWN_KEY: KNOWN_KEY,
    DEFAULTS: DEFAULTS,
    cookieKnown: cookieKnown,
    linkKnown: linkKnown,
    readCookie: readCookie,
    ownedChannelLanding: ownedChannelLanding,
    scrollDepth: scrollDepth,
    engagementMet: engagementMet,
    quietFor: quietFor,
    blockedPage: blockedPage,
    drawerOpen: drawerOpen,
    entryAllows: entryAllows,
    analytics: typeof PocketPopupAnalytics !== 'undefined' ? PocketPopupAnalytics : null,
    gate: gate
  };
});
